/**
 * ICU-lite message formatting for the web catalogues (locale/RTL audit
 * 2026-09-26: LANG-06 plurals and numbers, RTL-03 bidi isolation).
 *
 * Supported syntax — a subset of ICU MessageFormat, enough for the catalogues:
 *   {name}                           the value, as given (never number-formatted:
 *                                    years, ids and codes must stay as typed)
 *   {name, number}                   the value formatted for the locale
 *   {name, plural, =0 {…} one {…} …} CLDR plural categories via Intl.PluralRules
 *                                    (zero, one, two, few, many, other); `#` in
 *                                    the chosen branch is the formatted number
 *   {name, select, a {…} other {…}}  pick a branch by value
 * Apostrophes are ordinary text (no ICU quoting): French elision ("l'ajout")
 * is everywhere in fr.json and must not start a quoted run.
 *
 * Anything that does not parse is left as text rather than throwing, so a
 * malformed string can never take a screen down; the catalogue guard
 * (__tests__/catalogue-integrity.guard.spec.ts) keeps shipped strings well
 * formed.
 */

export type MessageValues = Record<string, string | number>

export interface FormatOptions {
  /**
   * The language the template is written in: plural categories, and number
   * formatting unless `numberLocale` is given. Default 'en'.
   */
  locale?: string
  /**
   * The locale numbers are grouped in, when it is not `locale` itself: an
   * English page on an en-IN or hi-IN browser groups 1,38,000, as the page's
   * own toLocaleString() does (lib/i18n/number-region.ts). Plural categories
   * still follow `locale`. Digits are Latin either way.
   */
  numberLocale?: string
  /**
   * Wrap every interpolated value in FSI…PDI (U+2068/U+2069). Set for
   * right-to-left pages, where an e-mail address or domain inside an Arabic
   * sentence otherwise reorders the words around it.
   */
  isolate?: boolean
}

const FSI = '⁨'
const PDI = '⁩'

interface Context {
  locale: string
  numberLocale: string
  isolate: boolean
}

const pluralRulesCache = new Map<string, Intl.PluralRules>()
const numberFormatCache = new Map<string, Intl.NumberFormat>()

function pluralRules(locale: string): Intl.PluralRules {
  let rules = pluralRulesCache.get(locale)
  if (!rules) {
    try {
      rules = new Intl.PluralRules(locale)
    } catch {
      rules = new Intl.PluralRules('en')
    }
    pluralRulesCache.set(locale, rules)
  }
  return rules
}

/**
 * Locale grouping, Western digits. Every table, date and chart in the app uses
 * Western digits, and so does the content-translation prompt; an Arabic-Indic
 * "٣" in toasts only would read as a different number system mid-screen, and
 * older engines still default 'ar' to Arabic-Indic.
 */
function formatNumber(value: number, locale: string): string {
  let format = numberFormatCache.get(locale)
  if (!format) {
    try {
      format = new Intl.NumberFormat(`${locale}-u-nu-latn`)
    } catch {
      format = new Intl.NumberFormat('en')
    }
    numberFormatCache.set(locale, format)
  }
  return format.format(value)
}

function toNumber(value: unknown): number | null {
  if (typeof value === 'number') return Number.isFinite(value) ? value : null
  if (typeof value === 'string' && value.trim() !== '') {
    const n = Number(value)
    return Number.isFinite(n) ? n : null
  }
  return null
}

/** Index of the `}` closing the `{` at `open`, or -1 when it never closes. */
function closingBrace(src: string, open: number): number {
  let depth = 0
  for (let i = open; i < src.length; i++) {
    if (src[i] === '{') depth++
    else if (src[i] === '}') {
      depth--
      if (depth === 0) return i
    }
  }
  return -1
}

const ARGUMENT = /^\s*(\w+)\s*(?:,\s*(\w+)\s*(?:,([\s\S]*))?)?$/
const SELECTOR = /^(=-?\d+(?:\.\d+)?|[A-Za-z_][\w-]*)/

/** `one {…} other {…}` → Map(selector → message). null when the text is not a branch list. */
function parseBranches(body: string): Map<string, string> | null {
  const branches = new Map<string, string>()
  let i = 0
  while (i < body.length) {
    while (i < body.length && /\s/.test(body[i])) i++
    if (i >= body.length) break
    const selector = SELECTOR.exec(body.slice(i))
    if (!selector) return null
    i += selector[0].length
    while (i < body.length && /\s/.test(body[i])) i++
    if (body[i] !== '{') return null
    const end = closingBrace(body, i)
    if (end === -1) return null
    branches.set(selector[1], body.slice(i + 1, end))
    i = end + 1
  }
  return branches.size > 0 ? branches : null
}

function render(src: string, values: MessageValues | undefined, ctx: Context, hash: string | null): string {
  let out = ''
  let i = 0
  while (i < src.length) {
    const ch = src[i]
    if (ch === '#' && hash !== null) {
      out += hash
      i++
      continue
    }
    if (ch !== '{') {
      out += ch
      i++
      continue
    }
    const end = closingBrace(src, i)
    if (end === -1) {
      // Unbalanced: the rest is text.
      out += src.slice(i)
      break
    }
    const replaced = renderArgument(src.slice(i + 1, end), values, ctx, hash)
    if (replaced === null) {
      // Not an argument (e.g. the outer brace of a {{mergeTag}}): keep the
      // brace as text and keep scanning inside it.
      out += ch
      i++
      continue
    }
    out += replaced
    i = end + 1
  }
  return out
}

function renderArgument(body: string, values: MessageValues | undefined, ctx: Context, hash: string | null): string | null {
  const match = ARGUMENT.exec(body)
  if (!match) return null
  const [, name, type, rest] = match
  const raw = `{${body}}`
  const wrap = (s: string) => (ctx.isolate ? `${FSI}${s}${PDI}` : s)
  const value = values?.[name]

  if (!type) return value === undefined ? raw : wrap(String(value))

  switch (type) {
    case 'number': {
      if (rest !== undefined) return raw
      const n = toNumber(value)
      return n === null ? raw : wrap(formatNumber(n, ctx.numberLocale))
    }
    case 'plural': {
      const n = toNumber(value)
      const branches = parseBranches(rest ?? '')
      if (n === null || !branches?.has('other')) return raw
      const chosen = branches.get(`=${n}`) ?? branches.get(pluralRules(ctx.locale).select(n)) ?? branches.get('other')!
      return render(chosen, values, ctx, wrap(formatNumber(n, ctx.numberLocale)))
    }
    case 'select': {
      const branches = parseBranches(rest ?? '')
      if (value === undefined || !branches?.has('other')) return raw
      return render(branches.get(String(value)) ?? branches.get('other')!, values, ctx, hash)
    }
    default:
      return null
  }
}

/** Format a catalogue string. */
export function formatMessage(template: string, values?: MessageValues, options: FormatOptions = {}): string {
  // Most strings carry no argument, and t() runs on every render: skip the scan.
  // Without a `{` there is nothing to replace (`#` only means something in a plural).
  if (!template.includes('{')) return template
  const locale = options.locale ?? 'en'
  return render(
    template,
    values,
    { locale, numberLocale: options.numberLocale ?? locale, isolate: options.isolate ?? false },
    null,
  )
}

/**
 * Every argument name a template uses — {name}, {n, number}, and the names
 * inside plural/select branches — for the placeholder-parity guard.
 */
export function argumentNames(template: string): string[] {
  const names = new Set<string>()
  const walk = (src: string) => {
    let i = 0
    while (i < src.length) {
      if (src[i] !== '{') {
        i++
        continue
      }
      const end = closingBrace(src, i)
      if (end === -1) return
      const match = ARGUMENT.exec(src.slice(i + 1, end))
      if (!match) {
        i++
        continue
      }
      const [, name, type, rest] = match
      names.add(name)
      if ((type === 'plural' || type === 'select') && rest) {
        for (const branch of parseBranches(rest)?.values() ?? []) walk(branch)
      }
      i = end + 1
    }
  }
  walk(template)
  return [...names]
}

/** Why a template would not render as intended; empty when it is well formed. */
export function parseProblems(template: string): string[] {
  const problems: string[] = []
  let depth = 0
  for (const ch of template) {
    if (ch === '{') depth++
    else if (ch === '}') depth--
    if (depth < 0) {
      problems.push('a "}" closes nothing')
      depth = 0
    }
  }
  if (depth > 0) problems.push('a "{" is never closed')
  if (problems.length > 0) return problems

  const walk = (src: string) => {
    let i = 0
    while (i < src.length) {
      if (src[i] !== '{') {
        i++
        continue
      }
      const end = closingBrace(src, i)
      const match = ARGUMENT.exec(src.slice(i + 1, end))
      if (!match) {
        i++
        continue
      }
      const [, name, type, rest] = match
      if (type === 'plural' || type === 'select') {
        const branches = parseBranches(rest ?? '')
        if (!branches) problems.push(`{${name}, ${type}} has no readable branches`)
        else if (!branches.has('other')) problems.push(`{${name}, ${type}} has no "other" branch`)
        for (const branch of branches?.values() ?? []) walk(branch)
      } else if (type === 'number' && rest !== undefined) {
        // formatMessage renders only a bare {n, number}; a style would show as raw text.
        problems.push(`{${name}, number, ${rest.trim()}}: number styles are not supported`)
      } else if (type && type !== 'number') {
        problems.push(`{${name}, ${type}}: unknown argument type`)
      }
      i = end + 1
    }
  }
  walk(template)
  return problems
}
